import { Link } from 'react-router-dom';
import {
  useChanges,
  ChangeLogEntry,
  ChangeRef,
  ChangeRefs,
  ResourceKind,
  InitiatorKind,
  ChangeAction,
} from '../api';

interface Props {
  resourceType?: ResourceKind;
  resourceId?: string;
  initiatorKind?: InitiatorKind;
  action?: ChangeAction;
  limit?: number;
  showResource?: boolean;
}

const KIND_LABEL: Record<string, string> = {
  lcr_sync: 'LCR sync',
  user_ui: 'UI',
  user_api: 'API',
  user_mcp: 'MCP',
  system: 'system',
};

const ACTION_BADGE: Record<string, string> = {
  created:  'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300',
  updated:  'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200',
  removed:  'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200',
  restored: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300',
};

const RESOURCE_PATH: Partial<Record<ResourceKind, string>> = {
  household: '/households',
  person:    '/people',
};

const REF_PATH: Record<string, string> = {
  household: '/households',
  person:    '/people',
};

const RESOURCE_LABEL: Record<ResourceKind, string> = {
  household: 'Household',
  person: 'Person',
  calling: 'Calling',
  interaction: 'Interaction',
  companionship: 'Companionship',
  companionship_assignment: 'Ministering assignment',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function ChangeLog({ resourceType, resourceId, initiatorKind, action, limit = 50, showResource = false }: Props) {
  const { data, isLoading, error } = useChanges({
    resource_type: resourceType,
    resource_id: resourceId,
    initiator_kind: initiatorKind,
    action,
    limit,
  });

  if (isLoading) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading change log…</p>;
  if (error) return <p className="text-sm text-red-600 dark:text-red-400">Error loading change log.</p>;
  const rows = data?.changes ?? [];
  const refs = data?.refs ?? {};
  if (rows.length === 0) return <p className="text-sm text-slate-500 dark:text-slate-400">No changes recorded.</p>;

  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-800 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      {rows.map((c) => <Row key={c.id} c={c} refs={refs} showResource={showResource} />)}
    </ul>
  );
}

function Row({ c, refs, showResource }: { c: ChangeLogEntry; refs: ChangeRefs; showResource: boolean }) {
  const initiator = formatInitiator(c);
  const parsedField = c.field ? parseField(c.field) : null;
  const isObjectDiff =
    isPlainObject(c.before_value) || isPlainObject(c.after_value);

  return (
    <li className="px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${ACTION_BADGE[c.action] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'}`}>
          {c.action}
        </span>
        {showResource && <ResourceLabel c={c} refs={refs} />}
        <span className="ml-auto whitespace-nowrap">{new Date(c.occurred_at).toLocaleString()}</span>
        <span className="text-slate-400 dark:text-slate-500">·</span>
        <span className="whitespace-nowrap">{initiator}</span>
      </div>

      {c.description && (
        <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">{c.description}</p>
      )}

      {parsedField && (
        <div className="mt-1 text-sm">
          <FieldHeader parsed={parsedField} refs={refs} />
          {!isObjectDiff && (
            <span className="text-slate-700 dark:text-slate-300">
              {' '}
              <FieldValue value={c.before_value} refs={refs} />
              {' → '}
              <FieldValue value={c.after_value} refs={refs} />
            </span>
          )}
        </div>
      )}

      {isObjectDiff && (
        <ObjectDiff before={c.before_value} after={c.after_value} refs={refs} />
      )}
    </li>
  );
}

function formatInitiator(c: ChangeLogEntry): string {
  if (c.initiator_kind === 'lcr_sync') {
    return `LCR sync${c.sync_run_id ? ` #${c.sync_run_id}` : ''}`;
  }
  const kind = KIND_LABEL[c.initiator_kind] ?? c.initiator_kind;
  if (!c.initiator_id) return kind;
  const id = c.initiator_id;
  // Show emails as-is, UUIDs truncated.
  const display = UUID_RE.test(id) ? id.slice(0, 8) : id;
  return `${kind} · ${display}`;
}

function ResourceLabel({ c, refs }: { c: ChangeLogEntry; refs: ChangeRefs }) {
  const path = RESOURCE_PATH[c.resource_type];
  const ref = refs[c.resource_id.toLowerCase()];
  const name = c.resource_name ?? ref?.name ?? null;
  const typeLabel = RESOURCE_LABEL[c.resource_type] ?? c.resource_type;
  const label = name ?? `${typeLabel} ${shortId(c.resource_id)}`;
  if (path) {
    return (
      <Link to={`${path}/${c.resource_id}`} className="font-medium text-slate-700 dark:text-slate-300 hover:underline" title={c.resource_id}>
        {label}
      </Link>
    );
  }
  return (
    <span className="font-medium text-slate-700 dark:text-slate-300" title={c.resource_id}>
      {label}
    </span>
  );
}

type ParsedField =
  | { kind: 'simple'; label: string }
  | { kind: 'assignment'; target: 'household' | 'person'; id: string }
  | { kind: 'link'; linkKind: string; id: string };

function parseField(field: string): ParsedField {
  // Composite: "assignment:household:UUID", "assignment:person:UUID", "link:KIND:UUID"
  const parts = field.split(':');
  if (parts.length === 3) {
    const [head, sub, id] = parts;
    if (head === 'assignment' && (sub === 'household' || sub === 'person')) {
      return { kind: 'assignment', target: sub, id };
    }
    if (head === 'link') {
      return { kind: 'link', linkKind: sub, id };
    }
  }
  return { kind: 'simple', label: prettifyFieldName(field) };
}

function FieldHeader({ parsed, refs }: { parsed: ParsedField; refs: ChangeRefs }) {
  if (parsed.kind === 'simple') {
    return (
      <span className="font-medium text-slate-700 dark:text-slate-300">{parsed.label}</span>
    );
  }
  if (parsed.kind === 'assignment') {
    return (
      <span>
        <span className="font-medium text-slate-700 dark:text-slate-300">
          {parsed.target === 'household' ? 'Household assignment' : 'Sister assignment'}
        </span>
        {' · '}
        <RefDisplay id={parsed.id} refs={refs} />
      </span>
    );
  }
  // link
  return (
    <span>
      <span className="font-medium text-slate-700 dark:text-slate-300">
        {capitalize(parsed.linkKind.replace(/_/g, ' '))} link
      </span>
      {' · '}
      <RefDisplay id={parsed.id} refs={refs} />
    </span>
  );
}

function prettifyFieldName(field: string): string {
  return field
    .replace(/_lcr$/, '')
    .replace(/_override$/, '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function FieldValue({ value, refs }: { value: unknown; refs: ChangeRefs }) {
  if (value === null || value === undefined) return <span className="text-slate-400 dark:text-slate-500">∅</span>;
  if (typeof value === 'string') {
    if (UUID_RE.test(value)) return <RefDisplay id={value} refs={refs} />;
    return <span className="font-mono text-xs">{value || '""'}</span>;
  }
  if (typeof value === 'boolean' || typeof value === 'number') {
    return <span className="font-mono text-xs">{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-slate-400 dark:text-slate-500">[]</span>;
    return (
      <span className="font-mono text-xs">
        [{value.map((v, i) => (
          <span key={i}>
            {i > 0 ? ', ' : ''}
            <FieldValue value={v} refs={refs} />
          </span>
        ))}]
      </span>
    );
  }
  return <span className="font-mono text-xs">{JSON.stringify(value)}</span>;
}

function ObjectDiff({
  before,
  after,
  refs,
}: {
  before: unknown;
  after: unknown;
  refs: ChangeRefs;
}) {
  const b = isPlainObject(before) ? before : {};
  const a = isPlainObject(after) ? after : {};
  const keys = Array.from(new Set([...Object.keys(b), ...Object.keys(a)]));
  if (keys.length === 0) return null;

  return (
    <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs">
      {keys.map((k) => {
        const bv = (b as Record<string, unknown>)[k];
        const av = (a as Record<string, unknown>)[k];
        const changed = JSON.stringify(bv) !== JSON.stringify(av);
        const hasBoth = bv !== undefined && av !== undefined;
        return (
          <div key={k} className="contents">
            <dt className="text-slate-500 dark:text-slate-400">{prettifyFieldName(k)}</dt>
            <dd className="text-slate-700 dark:text-slate-300">
              {changed && hasBoth ? (
                <>
                  <FieldValue value={bv} refs={refs} />
                  {' → '}
                  <FieldValue value={av} refs={refs} />
                </>
              ) : (
                <FieldValue value={av !== undefined ? av : bv} refs={refs} />
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function RefDisplay({ id, refs }: { id: string; refs: ChangeRefs }) {
  const ref: ChangeRef | undefined = refs[id.toLowerCase()];
  if (!ref) {
    return (
      <span className="font-mono text-xs text-slate-500 dark:text-slate-400" title={id}>
        {shortId(id)}
      </span>
    );
  }
  const label = ref.name ?? `${capitalize(ref.kind)} ${shortId(id)}`;
  const path = REF_PATH[ref.kind];
  if (path) {
    return (
      <Link to={`${path}/${id}`} className="text-slate-700 dark:text-slate-300 hover:underline" title={id}>
        {label}
      </Link>
    );
  }
  return (
    <span className="text-slate-700 dark:text-slate-300" title={id}>
      {label}
    </span>
  );
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
